/**
 * DashboardRenderer Component
 *
 * Main dashboard container that:
 * 1. Fetches dashboard configuration
 * 2. Manages tab state
 * 3. Renders appropriate template with charts
 * 4. Provides edit mode functionality (Feature #011)
 */

import { useEffect, useState, useCallback } from 'react';
import DashboardTabs from './DashboardTabs';
import TemplateFactory from './templates/TemplateFactory';
import EditToolbar from './EditToolbar';
import { EditModeProvider, useEditMode } from '../../contexts/EditModeContext';
import { getDashboardConfig, getCustomizedDashboard } from '../../services/dashboardService';
import './DashboardRenderer.css';

/**
 * Inner component that uses EditMode context
 */
const DashboardContent = ({ config, activeTabIndex, handleTabChange }) => {
  const { isEditMode, enterEditMode } = useEditMode();

  // Get active dashboard
  const activeDashboard = config.dashboards[activeTabIndex];
  const dashboardData = activeDashboard?.dashboard || activeDashboard;

  if (!dashboardData) {
    return (
      <div className="dashboard-renderer error">
        <div className="error-container">
          <h2>Invalid Dashboard Configuration</h2>
          <p>The selected dashboard configuration is invalid.</p>
        </div>
      </div>
    );
  }

  // Handle edit button click
  const handleEnterEditMode = () => {
    enterEditMode(
      dashboardData.id,
      dashboardData.template_id,
      config
    );
  };

  return (
    <div className="dashboard-renderer">
      {/* Tab navigation */}
      <DashboardTabs
        dashboards={config.dashboards}
        activeTabIndex={activeTabIndex}
        onTabChange={handleTabChange}
      />

      {/* Edit Toolbar (shown when in edit mode) */}
      {isEditMode && <EditToolbar />}

      {/* Dashboard header */}
      <div className="dashboard-header">
        <h1 className="dashboard-title">{dashboardData.title || dashboardData.tab}</h1>
        <p className="dashboard-subtitle">
          {dashboardData.tab} Dashboard
        </p>

        {/* Edit Dashboard Button (shown when NOT in edit mode) */}
        {!isEditMode && (
          <button
            className="edit-dashboard-button"
            onClick={handleEnterEditMode}
            title="Edit dashboard layout and cards"
          >
            ✏️ Edit Dashboard
          </button>
        )}
      </div>

      {/* Dashboard content */}
      <div className="dashboard-content">
        <TemplateFactory
          templateId={dashboardData.template_id}
          elements={dashboardData.elements}
          chartsConfig={config.charts}
          dashboardId={dashboardData.id}
        />
      </div>
    </div>
  );
};

/**
 * Main DashboardRenderer component with EditModeProvider
 */
const DashboardRenderer = () => {
  const [baseConfig, setBaseConfig] = useState(null); // Base YAML config (for tabs)
  const [config, setConfig] = useState(null); // Active config (with customizations)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Force refresh counter

  // Load base config once on mount
  useEffect(() => {
    const fetchBaseConfig = async () => {
      try {
        setLoading(true);
        const response = await getDashboardConfig();

        if (response.success && response.data) {
          setBaseConfig(response.data);
          setConfig(response.data); // Initialize with base config
          console.log('Base dashboard config loaded:', response.data);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching dashboard config:', err);
        setError(err.message || 'Failed to load dashboard configuration');
      } finally {
        setLoading(false);
      }
    };

    fetchBaseConfig();
  }, []);

  // Load customized dashboard when tab changes or refresh is triggered
  useEffect(() => {
    if (!baseConfig || !baseConfig.dashboards || baseConfig.dashboards.length === 0) {
      return;
    }

    const fetchCustomizedDashboard = async () => {
      try {
        const activeDashboard = baseConfig.dashboards[activeTabIndex];
        const dashboardData = activeDashboard?.dashboard || activeDashboard;

        if (!dashboardData || !dashboardData.id) {
          console.warn('No dashboard ID found for tab', activeTabIndex);
          return;
        }

        console.log(`Loading customizations for dashboard: ${dashboardData.id} (trigger: ${refreshTrigger})`);

        // Fetch customized dashboard
        const customizedData = await getCustomizedDashboard(dashboardData.id);

        console.log('Customized data received:', customizedData);

        // Merge customized data into config
        const updatedConfig = {
          ...baseConfig,
          dashboards: baseConfig.dashboards.map((dash, idx) => {
            if (idx === activeTabIndex) {
              // Replace this dashboard with customized version
              return customizedData.dashboards.find(d => {
                const dData = d?.dashboard || d;
                return dData.id === dashboardData.id;
              }) || dash;
            }
            return dash;
          }),
          // Update charts from customized data (in case card content changed)
          charts: customizedData.charts || baseConfig.charts
        };

        setConfig(updatedConfig);
        console.log('Customized dashboard applied to config');
      } catch (err) {
        console.error('Error loading customized dashboard:', err);
        // Fallback to base config if customization fails
        setConfig(baseConfig);
      }
    };

    fetchCustomizedDashboard();
  }, [activeTabIndex, baseConfig, refreshTrigger]);

  const handleTabChange = (index) => {
    setActiveTabIndex(index);
  };

  // Callback to refresh dashboard after save
  const handleSaveComplete = useCallback((dashboardId) => {
    console.log('Save completed for dashboard:', dashboardId);
    console.log('Triggering refresh to load customizations...');
    // Trigger a re-fetch by incrementing the refresh counter
    setRefreshTrigger(prev => prev + 1);
  }, []);

  if (loading) {
    return (
      <div className="dashboard-renderer loading">
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading dashboards...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-renderer error">
        <div className="error-container">
          <h2>Error Loading Dashboard</h2>
          <p className="error-message">{error}</p>
          <button
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!config || !config.dashboards || config.dashboards.length === 0) {
    return (
      <div className="dashboard-renderer no-data">
        <div className="no-data-container">
          <h2>No Dashboards Available</h2>
          <p>No dashboard configurations found.</p>
        </div>
      </div>
    );
  }

  // Wrap in EditModeProvider with save callback
  return (
    <EditModeProvider onSaveComplete={handleSaveComplete}>
      <DashboardContent
        config={config}
        activeTabIndex={activeTabIndex}
        handleTabChange={handleTabChange}
      />
    </EditModeProvider>
  );
};

export default DashboardRenderer;
